import React, { useCallback, useEffect, useState, useRef, Component } from 'react';
import { useProjectContext } from '../../hooks/useProjectContext';
import { runTool } from '../../services/toolRunner';
import { BookOpen, Search, FileText, ChevronRight, ChevronDown, X, ExternalLink, Clock, AlertCircle, Folder, Tag, Maximize2, Minimize2, GripVertical, Sun, Moon, Monitor, Users } from 'lucide-react';
import { SummarizeButton } from '../shared/SummarizeButton';
// ─── Interfaces ───────────────────────────────────────────────────────────────
interface Space {
  id: string;
  key: string;
  name: string;
  type: string;
  pageCount?: number;
}
interface Page {
  id: string;
  title: string;
  excerpt?: string;
  lastModified?: string;
  author?: string;
  spaceKey?: string;
  labels?: string[];
}
interface PageDetail extends Page {
  contentHtml: string;
  url: string;
}
type PreviewTheme = 'dark' | 'light' | 'system';
interface CachedPage {
  page: PageDetail;
  rawHtml: string; // Original HTML from Confluence (unmodified)
}
// ─── Helpers ──────────────────────────────────────────────────────────────────
function toArray(val: unknown): any[] {
  if (Array.isArray(val)) return val;
  return [];
}
function extractData(data: any): any {
  if (!data) return [];
  if (Array.isArray(data)) return data;
  if (Array.isArray(data.results)) return data.results;
  if (Array.isArray(data.content) && data.content.length > 0) {
    const firstContent = data.content[0];
    if (firstContent?.type === 'text' && typeof firstContent.text === 'string') {
      try {
        return extractData(JSON.parse(firstContent.text));
      } catch {
        return [];
      }
    }
  }
  if (data.data) return extractData(data.data);
  if (data.result) return extractData(data.result);
  if (Array.isArray(data.values)) return data.values;
  if (data.id) return [data];
  return [];
}
function extractObject(data: any): any {
  if (!data) return null;
  if (data.id || data.title || data.body) return data;
  if (Array.isArray(data.content) && data.content.length > 0) {
    const firstContent = data.content[0];
    if (firstContent?.type === 'text' && typeof firstContent.text === 'string') {
      try {
        return extractObject(JSON.parse(firstContent.text));
      } catch {
        return data;
      }
    }
  }
  if (data.data) return extractObject(data.data);
  if (data.result) return extractObject(data.result);
  return data;
}
function cleanConfluenceHtml(html: string): string {
  if (!html) return '<p class="text-slate-500 italic">Sin contenido disponible</p>';
  let c = html;
  c = c.replace(/<ac:structured-macro[^>]*ac:name="(info|note|warning|tip|expand|panel|code)"[^>]*>/gi, (_, name) => {
    const colors: Record<string, string> = {
      info: 'border-blue-500/30 bg-blue-500/5',
      note: 'border-yellow-500/30 bg-yellow-500/5',
      warning: 'border-red-500/30 bg-red-500/5',
      tip: 'border-green-500/30 bg-green-500/5',
      expand: 'border-slate-600/30 bg-slate-800/30',
      panel: 'border-slate-600/30 bg-slate-800/20',
      code: 'border-slate-600/30 bg-slate-950'
    };
    return `<div class="border-l-2 ${colors[name] || 'border-slate-600/30 bg-slate-800/20'} rounded-r-lg p-3 my-3">`;
  });
  c = c.replace(/<\/ac:structured-macro>/gi, '</div>');
  c = c.replace(/<\/?ac:rich-text-body>/gi, '');
  c = c.replace(/<ac:plain-text-body><!\[CDATA\[([\s\S]*?)\]\]><\/ac:plain-text-body>/gi, '<pre class="bg-slate-950 border border-slate-800 rounded-lg p-3 text-sm overflow-x-auto"><code>$1</code></pre>');
  c = c.replace(/<\/?ac:plain-text-body>/gi, '');
  c = c.replace(/<ac:parameter[^>]*>[\s\S]*?<\/ac:parameter>/gi, '');
  c = c.replace(/<\/?ac:[^>]*>/gi, '');
  c = c.replace(/<\/?ri:[^>]*>/gi, '');
  c = c.replace(/<!\[CDATA\[([\s\S]*?)\]\]>/gi, '$1');
  c = c.replace(/<ac:emoticon ac:name="([^"]*)"[^/]*\/>/gi, (_, name) => {
    const e: Record<string, string> = {
      'blue-star': '⭐',
      tick: '✅',
      cross: '❌',
      warning: '⚠️',
      information: 'ℹ️',
      'thumbs-up': '👍',
      smile: '😊',
      'light-on': '💡'
    };
    return e[name] || '';
  });
  c = c.replace(/<table/gi, '<table class="w-full border-collapse my-4 text-sm"');
  c = c.replace(/<th/gi, '<th class="text-left p-2 border border-slate-700 bg-slate-800/50 text-slate-200 font-semibold text-xs"');
  c = c.replace(/<td/gi, '<td class="p-2 border border-slate-800 text-slate-300 text-xs"');
  c = c.replace(/<p>\s*<\/p>/gi, '');
  c = c.replace(/(<br\s*\/?>){3,}/gi, '<br/><br/>');
  return c;
}
/**
 * Clean Confluence "view" format HTML for dark theme.
 * The view format has pre-rendered HTML with inline styles for light backgrounds.
 * We need to convert those to dark-theme equivalents.
 */
function cleanViewHtml(html: string): string {
  if (!html) return '<p class="text-slate-500 italic">Sin contenido disponible</p>';
  let c = html;
  // NUCLEAR APPROACH: Strip ALL inline background-color and color styles,
  // then let Tailwind prose classes handle everything.
  // This catches hex, rgb, rgba, named colors - everything.
  // First, handle semantic panels by detecting known Confluence panel patterns
  // and wrapping them with our own classes before stripping
  // Strip ALL background-color declarations
  c = c.replace(/background-color:\s*[^;"']+/gi, 'background-color: transparent');
  // Strip ALL color declarations (text color) and replace with light text
  c = c.replace(/(?<=[;"\s])color:\s*[^;"']+/gi, 'color: #e2e8f0');
  // Fix table borders
  c = c.replace(/border[^:]*:\s*[^;"']*#[0-9a-fA-F]{3,8}[^;"']*/gi, match => {
    if (match.includes('border-left') || match.includes('border-right') || match.includes('border-top') || match.includes('border-bottom')) {
      return match.replace(/#[0-9a-fA-F]{3,8}/gi, 'rgba(51,65,85,0.5)');
    }
    return match.replace(/#[0-9a-fA-F]{3,8}/gi, 'rgba(51,65,85,0.5)');
  });
  // Clean up empty paragraphs
  c = c.replace(/<p>\s*<\/p>/gi, '');
  c = c.replace(/(<br\s*\/?>){3,}/gi, '<br/><br/>');
  return c;
}
/**
 * Clean Confluence "view" format HTML for light theme.
 * Normalizes Atlassian-specific inline styles to clean, consistent light-theme equivalents.
 */
function cleanLightHtml(html: string): string {
  if (!html) return '<p style="color:#64748b;font-style:italic">Sin contenido disponible</p>';
  let c = html;
  // NUCLEAR APPROACH for light theme too: strip all inline styles
  // and let prose classes handle styling
  // Strip ALL background-color → transparent
  c = c.replace(/background-color:\s*[^;"']+/gi, 'background-color: transparent');
  // Strip ALL color → dark text
  c = c.replace(/(?<=[;"\s])color:\s*[^;"']+/gi, 'color: #1e293b');
  // Fix table borders for light
  c = c.replace(/border[^:]*:\s*[^;"']*#[0-9a-fA-F]{3,8}[^;"']*/gi, match => {
    return match.replace(/#[0-9a-fA-F]{3,8}/gi, '#e2e8f0');
  });
  // Clean up empty paragraphs
  c = c.replace(/<p>\s*<\/p>/gi, '');
  c = c.replace(/(<br\s*\/?>){3,}/gi, '<br/><br/>');
  return c;
}
function atlasDocToHtml(doc: any): string {
  if (!doc || !doc.content) return '';
  function r(node: any): string {
    if (!node) return '';
    if (typeof node === 'string') return node;
    const ch = Array.isArray(node.content) ? node.content.map(r).join('') : '';
    const t = node.text || '';
    switch (node.type) {
      case 'doc':
        return ch;
      case 'paragraph':
        return `<p>${ch || t}</p>`;
      case 'heading':
        return `<h${node.attrs?.level || 2}>${ch || t}</h${node.attrs?.level || 2}>`;
      case 'text':
        {
          let res = node.text || '';
          if (Array.isArray(node.marks)) {
            for (const m of node.marks) {
              if (m.type === 'strong') res = `<strong>${res}</strong>`;
              if (m.type === 'em') res = `<em>${res}</em>`;
              if (m.type === 'code') res = `<code>${res}</code>`;
              if (m.type === 'link') res = `<a href="${m.attrs?.href || '#'}" target="_blank">${res}</a>`;
              if (m.type === 'textColor') res = `<span style="color:${m.attrs?.color || 'inherit'}">${res}</span>`;
            }
          }
          return res;
        }
      case 'bulletList':
        return `<ul>${ch}</ul>`;
      case 'orderedList':
        return `<ol>${ch}</ol>`;
      case 'listItem':
        return `<li>${ch}</li>`;
      case 'codeBlock':
        return `<pre class="bg-slate-950 border border-slate-800 rounded-lg p-3 text-sm overflow-x-auto"><code>${ch || t}</code></pre>`;
      case 'blockquote':
        return `<blockquote>${ch}</blockquote>`;
      case 'rule':
        return '<hr/>';
      case 'table':
        return `<table class="w-full border-collapse my-4">${ch}</table>`;
      case 'tableRow':
        return `<tr>${ch}</tr>`;
      case 'tableHeader':
        return `<th class="text-left p-2 border border-slate-700 bg-slate-800/50 text-slate-200 font-semibold text-xs">${ch}</th>`;
      case 'tableCell':
        return `<td class="p-2 border border-slate-800 text-slate-300 text-xs">${ch}</td>`;
      case 'emoji':
        return node.attrs?.text || '';
      case 'status':
        return `<span class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium" style="background-color:${node.attrs?.color || '#666'}22;color:${node.attrs?.color || '#999'}">${node.attrs?.text || ch}</span>`;
      case 'expand':
        return `<details class="my-3 border border-slate-700 rounded-lg"><summary class="p-2 cursor-pointer text-slate-300 font-medium">${node.attrs?.title || 'Expandir'}</summary><div class="p-3 border-t border-slate-700">${ch}</div></details>`;
      case 'hardBreak':
        return '<br/>';
      case 'mediaSingle':
      case 'media':
        return `<div class="my-3 p-4 bg-slate-800/30 border border-slate-700 rounded-lg text-center text-xs text-slate-500">📎 Archivo adjunto</div>`;
      default:
        return ch || t;
    }
  }
  return r(doc);
}
function extractExcerpt(html: string | undefined, maxLen = 140): string {
  if (!html) return '';
  let text = html.replace(/<[^>]*>/g, ' ').replace(/<!\[CDATA\[|\]\]>/g, '').replace(/\s+/g, ' ').trim().replace(/\{[^}]+\}/g, '');
  return text.length > maxLen ? text.substring(0, maxLen) + '…' : text;
}
function formatDate(dateStr: string | undefined): string {
  if (!dateStr) return '';
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return '';
    return d.toLocaleDateString('es', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  } catch {
    return '';
  }
}
// ─── Mock Data ────────────────────────────────────────────────────────────────
const MOCK_SPACES: Space[] = [{
  id: '1',
  key: 'ENG',
  name: 'Engineering',
  type: 'global',
  pageCount: 24
}, {
  id: '2',
  key: 'PROD',
  name: 'Product',
  type: 'global',
  pageCount: 18
}, {
  id: '3',
  key: 'RETRO',
  name: 'Sprint Retrospectives',
  type: 'global',
  pageCount: 12
}, {
  id: '4',
  key: 'ADR',
  name: 'Architecture Decisions',
  type: 'global',
  pageCount: 8
}];
const MOCK_PAGES: PageDetail[] = [{
  id: '101',
  title: 'API Authentication Design',
  excerpt: 'Technical design document for the new JWT-based authentication system using Cognito.',
  lastModified: '2024-03-15',
  author: 'Miguel Silva',
  spaceKey: 'ENG',
  labels: ['auth', 'backend', 'design'],
  url: '#',
  contentHtml: `<h2>Overview</h2><p>This document outlines the migration from session-based auth to JWT tokens using AWS Cognito.</p><h3>Requirements</h3><ul><li>Stateless authentication</li><li>Support for mobile and web clients</li><li>Role-based access control (RBAC)</li></ul>`
}, {
  id: '102',
  title: 'Deployment Runbook',
  excerpt: 'Step-by-step guide for deploying the application to production environments.',
  lastModified: '2024-03-10',
  author: 'Mateo López',
  spaceKey: 'ENG',
  labels: ['devops', 'deployment'],
  url: '#',
  contentHtml: `<h2>Production Deployment Runbook</h2><p>Follow these steps strictly when deploying to production.</p><h3>Pre-deployment Checklist</h3><ul><li>All tests passing in CI</li><li>Staging signed off by QA</li></ul>`
}, {
  id: '103',
  title: 'Coding Standards',
  excerpt: 'Frontend and backend coding conventions and best practices.',
  lastModified: '2024-02-28',
  author: 'Sofia Chen',
  spaceKey: 'ENG',
  labels: ['standards', 'frontend'],
  url: '#',
  contentHtml: `<h2>Coding Standards</h2><h3>TypeScript Guidelines</h3><ul><li>Always use strict mode.</li><li>Prefer interface over type.</li></ul>`
}, {
  id: '201',
  title: 'Q2 Roadmap',
  excerpt: 'Strategic goals and major feature deliverables for Q2.',
  lastModified: '2024-03-20',
  author: 'Ana García',
  spaceKey: 'PROD',
  labels: ['roadmap', 'planning'],
  url: '#',
  contentHtml: `<h2>Q2 2024 Roadmap</h2><p>Primary focus: user retention and enterprise features.</p>`
}, {
  id: '202',
  title: 'Feature Spec: Notifications',
  excerpt: 'Product requirements for the notification system.',
  lastModified: '2024-03-18',
  author: 'Ana García',
  spaceKey: 'PROD',
  labels: ['spec', 'feature'],
  url: '#',
  contentHtml: `<h2>Notification System Specification</h2><p>In-app and email notification system requirements.</p>`
}, {
  id: '301',
  title: 'Sprint 23 Retrospective',
  excerpt: 'Notes and action items from Sprint 23 retro.',
  lastModified: '2024-03-09',
  author: 'Carlos Ruiz',
  spaceKey: 'RETRO',
  labels: ['retro', 'sprint'],
  url: '#',
  contentHtml: `<h2>Sprint 23 Retrospective</h2><p><strong>Date:</strong> March 8, 2024</p>`
}, {
  id: '401',
  title: 'ADR-001: Database Selection',
  excerpt: 'Decision record for choosing PostgreSQL.',
  lastModified: '2023-11-15',
  author: 'Carlos Ruiz',
  spaceKey: 'ADR',
  labels: ['adr', 'database'],
  url: '#',
  contentHtml: `<h2>ADR-001: Primary Database Selection</h2><p>We will use PostgreSQL (Amazon RDS).</p>`
}, {
  id: '402',
  title: 'ADR-002: Microservices',
  excerpt: 'Decision to split the monolith into microservices.',
  lastModified: '2024-01-10',
  author: 'Carlos Ruiz',
  spaceKey: 'ADR',
  labels: ['adr', 'architecture'],
  url: '#',
  contentHtml: `<h2>ADR-002: Transition to Microservices</h2><p>Gradually migrate based on DDD principles.</p>`
}];
// ─── Page Content Cache ───────────────────────────────────────────────────────
const pageCache = new Map<string, CachedPage>();
// ─── Component ────────────────────────────────────────────────────────────────
export function ConfluencePanel() {
  const {
    dataMode
  } = useProjectContext();
  const isLive = dataMode === 'live';
  const [spaces, setSpaces] = useState<Space[]>([]);
  const [spacesLoading, setSpacesLoading] = useState(false);
  const [spacesError, setSpacesError] = useState<string | null>(null);
  const [selectedSpace, setSelectedSpace] = useState<Space | null>(null);
  const [pages, setPages] = useState<Page[]>([]);
  const [pagesLoading, setPagesLoading] = useState(false);
  const [pagesError, setPagesError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [activeFilter, setActiveFilter] = useState('Todos');
  const [selectedPage, setSelectedPage] = useState<PageDetail | null>(null);
  const [rawPageHtml, setRawPageHtml] = useState<string>(''); // Store raw HTML for theme switching
  const [pageLoading, setPageLoading] = useState(false);
  const [pageError, setPageError] = useState<string | null>(null);
  // Preview panel state
  const [previewWidth, setPreviewWidth] = useState(500);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const resizeRef = useRef<{
    startX: number;
    startWidth: number;
  } | null>(null);
  // Theme state - default to dark to match app theme
  const [previewTheme, setPreviewTheme] = useState<PreviewTheme>(() => {
    try {
      return localStorage.getItem('confluence-preview-theme') as PreviewTheme || 'dark';
    } catch {
      return 'dark';
    }
  });
  const [systemDark, setSystemDark] = useState(() => window.matchMedia?.('(prefers-color-scheme: dark)').matches ?? true);
  const filterOptions = ['Todos', 'Sprint Docs', 'Decisiones', 'Retros'];
  // Resolve actual theme
  const resolvedTheme = previewTheme === 'system' ? systemDark ? 'dark' : 'light' : previewTheme;
  const isDarkPreview = resolvedTheme === 'dark';
  // Listen for system theme changes
  useEffect(() => {
    const mq = window.matchMedia?.('(prefers-color-scheme: dark)');
    if (!mq) return;
    const handler = (e: MediaQueryListEvent) => setSystemDark(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);
  // Persist theme preference
  const changeTheme = (theme: PreviewTheme) => {
    setPreviewTheme(theme);
    try {
      localStorage.setItem('confluence-preview-theme', theme);
    } catch {}
    // Re-process content for new theme
    if (rawPageHtml && selectedPage) {
      const processed = isDarkPreviewFor(theme) ? cleanViewHtml(rawPageHtml) : cleanLightHtml(rawPageHtml);
      setSelectedPage({
        ...selectedPage,
        contentHtml: processed
      });
    }
  };
  const isDarkPreviewFor = (theme: PreviewTheme) => {
    if (theme === 'system') return systemDark;
    return theme === 'dark';
  };
  // Re-process content when theme changes
  useEffect(() => {
    if (!rawPageHtml || !selectedPage) return;
    const processed = isDarkPreview ? cleanViewHtml(rawPageHtml) : cleanLightHtml(rawPageHtml);
    setSelectedPage(prev => prev ? {
      ...prev,
      contentHtml: processed
    } : null);
  }, [resolvedTheme]);
  // ─── Resize handlers ─────────────────────────────────────────────────
  const handleResizeStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizing(true);
    resizeRef.current = {
      startX: e.clientX,
      startWidth: previewWidth
    };
    const handleMouseMove = (ev: MouseEvent) => {
      if (!resizeRef.current) return;
      const diff = resizeRef.current.startX - ev.clientX;
      const newWidth = Math.min(Math.max(resizeRef.current.startWidth + diff, 350), 1200);
      setPreviewWidth(newWidth);
    };
    const handleMouseUp = () => {
      setIsResizing(false);
      resizeRef.current = null;
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  }, [previewWidth]);
  // ─── Keyboard shortcut: Escape exits fullscreen ───────────────────────
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isFullscreen) setIsFullscreen(false);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isFullscreen]);
  // ─── Fetch spaces ─────────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    async function fetchSpaces() {
      setSpacesLoading(true);
      setSpacesError(null);
      try {
        if (isLive) {
          const res = await runTool('confluence_get_spaces', {
            limit: 250
          });
          if (cancelled) return;
          if (!res.success) throw new Error(res.error || 'Error fetching spaces');
          const data = toArray(extractData(res.data));
          const mapped = data.map((s: any) => ({
            id: String(s.id || ''),
            key: String(s.key || ''),
            name: String(s.name || s.key || 'Sin nombre'),
            type: String(s.type || 'global'),
            pageCount: s.pageCount
          }));
          setSpaces(mapped);
          if (mapped.length > 0) setSelectedSpace(mapped[0]);
        } else {
          await new Promise(r => setTimeout(r, 300));
          if (cancelled) return;
          setSpaces(MOCK_SPACES);
          setSelectedSpace(MOCK_SPACES[0]);
        }
      } catch (err: any) {
        if (!cancelled) setSpacesError(err.message);
      } finally {
        if (!cancelled) setSpacesLoading(false);
      }
    }
    fetchSpaces();
    return () => {
      cancelled = true;
    };
  }, [isLive]);
  // ─── Fetch pages ──────────────────────────────────────────────────────
  useEffect(() => {
    if (!selectedSpace || isSearching) return;
    let cancelled = false;
    async function fetchPages() {
      setPagesLoading(true);
      setPagesError(null);
      try {
        if (isLive) {
          const res = await runTool('confluence_get_pages_in_space', {
            spaceId: selectedSpace!.key || selectedSpace!.id,
            limit: 20
          });
          if (cancelled) return;
          if (!res.success) throw new Error(res.error || 'Error fetching pages');
          const rawData = toArray(extractData(res.data));
          const formattedPages: Page[] = rawData.map((p: any) => {
            const bodyText = p.body?.storage?.value || p.body?.view?.value || '';
            const excerpt = p.excerpt || extractExcerpt(bodyText) || p.description || '';
            const date = p.version?.when || p.createdAt || p.lastModified || '';
            const author = p.version?.by?.publicName || p.version?.by?.displayName || p.authorId || '';
            return {
              id: String(p.id || ''),
              title: String(p.title || 'Sin título'),
              excerpt,
              lastModified: formatDate(date),
              author: author || 'Unknown',
              spaceKey: selectedSpace!.key,
              labels: Array.isArray(p.labels?.results) ? p.labels.results.map((l: any) => l.name || l.label || String(l)) : []
            };
          });
          setPages(formattedPages);
        } else {
          await new Promise(r => setTimeout(r, 300));
          if (cancelled) return;
          let filtered = MOCK_PAGES.filter(p => p.spaceKey === selectedSpace!.key);
          if (activeFilter === 'Sprint Docs') filtered = filtered.filter(p => p.labels?.includes('sprint') || p.labels?.includes('spec'));else if (activeFilter === 'Decisiones') filtered = filtered.filter(p => p.labels?.includes('adr') || p.labels?.includes('design'));else if (activeFilter === 'Retros') filtered = filtered.filter(p => p.labels?.includes('retro'));
          setPages(filtered);
        }
      } catch (err: any) {
        if (!cancelled) setPagesError(err.message);
      } finally {
        if (!cancelled) setPagesLoading(false);
      }
    }
    fetchPages();
    return () => {
      cancelled = true;
    };
  }, [selectedSpace, isLive, isSearching, activeFilter]);
  // ─── Search ───────────────────────────────────────────────────────────
  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!searchQuery.trim()) {
      clearSearch();
      return;
    }
    setIsSearching(true);
    setPagesLoading(true);
    setPagesError(null);
    setSelectedPage(null);
    try {
      if (isLive) {
        const res = await runTool('confluence_search_content', {
          query: searchQuery,
          limit: 20
        });
        if (!res.success) throw new Error(res.error || 'Error searching');
        const rawData = toArray(extractData(res.data));
        setPages(rawData.map((item: any) => {
          const p = item.content || item;
          return {
            id: String(p.id || ''),
            title: String(p.title || 'Sin título'),
            excerpt: item.excerpt?.replace(/@@@hl@@@/g, '').replace(/@@@endhl@@@/g, '').replace(/<[^>]*>?/gm, '') || '',
            lastModified: p.version?.when ? new Date(p.version.when).toLocaleDateString() : '',
            spaceKey: p.space?.key || ''
          };
        }));
      } else {
        await new Promise(r => setTimeout(r, 300));
        const q = searchQuery.toLowerCase();
        setPages(MOCK_PAGES.filter(p => p.title.toLowerCase().includes(q) || p.excerpt?.toLowerCase().includes(q)));
      }
    } catch (err: any) {
      setPagesError(err.message);
    } finally {
      setPagesLoading(false);
    }
  };
  const clearSearch = () => {
    setSearchQuery('');
    setIsSearching(false);
    setSelectedPage(null);
  };
  // ─── Load page detail (with cache + single API call) ──────────────────
  const handlePageClick = async (page: Page) => {
    // Check cache first
    const cached = pageCache.get(page.id);
    if (cached) {
      setRawPageHtml(cached.rawHtml);
      const processed = isDarkPreview ? cleanViewHtml(cached.rawHtml) : cleanLightHtml(cached.rawHtml);
      setSelectedPage({
        ...cached.page,
        contentHtml: processed
      });
      return;
    }
    setSelectedPage({
      ...page,
      contentHtml: '',
      url: ''
    });
    setRawPageHtml('');
    setPageLoading(true);
    setPageError(null);
    try {
      if (isLive) {
        let rawHtml = '';
        let pageData: any = {};
        // Single call with view format (pre-rendered HTML)
        const res = await runTool('confluence_get_page', {
          pageId: page.id,
          bodyFormat: 'view'
        });
        if (res.success) {
          pageData = extractObject(res.data) || {};
          rawHtml = pageData.body?.view?.value || '';
        }
        // Fallback: storage format only if view returned nothing
        if (!rawHtml) {
          const storageRes = await runTool('confluence_get_page', {
            pageId: page.id,
            bodyFormat: 'storage'
          });
          if (storageRes.success) {
            pageData = extractObject(storageRes.data) || {};
            // For storage format, always clean it (it has ac:* tags)
            rawHtml = cleanConfluenceHtml(pageData.body?.storage?.value || '');
          }
        }
        if (!rawHtml) {
          rawHtml = '<p style="color:#94a3b8;font-style:italic">Esta página no tiene contenido.</p>';
        }
        const baseUrl = pageData._links?.base || '';
        const webui = pageData._links?.webui || '';
        const contentHtml = isDarkPreview ? cleanViewHtml(rawHtml) : cleanLightHtml(rawHtml);
        const detail: PageDetail = {
          ...page,
          contentHtml,
          url: baseUrl && webui ? baseUrl.includes('http') ? `${baseUrl}${webui}` : `https://${baseUrl}${webui}` : ''
        };
        // Cache raw HTML for theme switching
        setRawPageHtml(rawHtml);
        pageCache.set(page.id, {
          page: detail,
          rawHtml
        });
        setSelectedPage(detail);
      } else {
        await new Promise(r => setTimeout(r, 200));
        const fullPage = MOCK_PAGES.find(p => p.id === page.id);
        if (fullPage) {
          setRawPageHtml(fullPage.contentHtml);
          setSelectedPage(fullPage);
        } else throw new Error('Page not found');
      }
    } catch (err: any) {
      setPageError(err.message);
    } finally {
      setPageLoading(false);
    }
  };
  // ─── Spaces filtering ────────────────────────────────────────────────
  const [spaceSearch, setSpaceSearch] = useState('');
  const [showPersonalSpaces, setShowPersonalSpaces] = useState(false);
  const safeSpacesAll = Array.isArray(spaces) ? spaces : [];
  const filteredSpaces = spaceSearch.trim() ? safeSpacesAll.filter(s => s.name.toLowerCase().includes(spaceSearch.toLowerCase()) || s.key.toLowerCase().includes(spaceSearch.toLowerCase())) : safeSpacesAll;
  const globalSpaces = filteredSpaces.filter(s => s.type !== 'personal');
  const personalSpaces = filteredSpaces.filter(s => s.type === 'personal');
  // ─── Render ───────────────────────────────────────────────────────────
  const safeSpaces = filteredSpaces;
  const safePages = Array.isArray(pages) ? pages : [];
  const showPreview = !!selectedPage;
  const darkProseClasses = `confluence-content prose prose-invert prose-sm max-w-none
    prose-headings:text-slate-100 prose-headings:font-bold prose-headings:mt-6 prose-headings:mb-3
    prose-h2:text-lg prose-h2:border-b prose-h2:border-slate-800 prose-h2:pb-2 prose-h3:text-base
    prose-p:text-slate-300 prose-p:leading-relaxed prose-p:my-2
    prose-a:text-indigo-400 hover:prose-a:text-indigo-300 prose-a:no-underline hover:prose-a:underline
    prose-strong:text-slate-100 prose-em:text-slate-400
    prose-code:text-indigo-300 prose-code:bg-slate-800/80 prose-code:px-1.5 prose-code:py-0.5 prose-code:rounded prose-code:text-xs prose-code:font-mono
    prose-pre:bg-slate-950 prose-pre:border prose-pre:border-slate-800 prose-pre:rounded-lg prose-pre:my-4
    prose-ul:my-2 prose-ol:my-2 prose-li:text-slate-300 prose-li:my-0.5
    prose-th:text-slate-200 prose-th:bg-slate-800/50 prose-th:p-2 prose-th:text-xs prose-th:font-semibold prose-th:border prose-th:border-slate-700
    prose-td:text-slate-400 prose-td:p-2 prose-td:text-xs prose-td:border prose-td:border-slate-800
    prose-blockquote:border-l-2 prose-blockquote:border-indigo-500 prose-blockquote:bg-indigo-500/5 prose-blockquote:py-2 prose-blockquote:px-4 prose-blockquote:rounded-r-lg prose-blockquote:my-4 prose-blockquote:not-italic
    prose-hr:border-slate-800 prose-hr:my-6
    prose-img:rounded-lg prose-img:border prose-img:border-slate-700`;
  const lightProseClasses = `confluence-content prose prose-sm max-w-none
    prose-headings:text-slate-900 prose-headings:font-bold prose-headings:mt-6 prose-headings:mb-3
    prose-h2:text-lg prose-h2:border-b prose-h2:border-slate-200 prose-h2:pb-2 prose-h3:text-base
    prose-p:text-slate-700 prose-p:leading-relaxed prose-p:my-2
    prose-a:text-indigo-600 hover:prose-a:text-indigo-500 prose-a:no-underline hover:prose-a:underline
    prose-strong:text-slate-900 prose-em:text-slate-600
    prose-code:text-indigo-600 prose-code:bg-slate-100 prose-code:px-1.5 prose-code:py-0.5 prose-code:rounded prose-code:text-xs prose-code:font-mono
    prose-pre:bg-slate-50 prose-pre:border prose-pre:border-slate-200 prose-pre:rounded-lg prose-pre:my-4
    prose-ul:my-2 prose-ol:my-2 prose-li:text-slate-700 prose-li:my-0.5
    prose-th:text-slate-700 prose-th:bg-slate-50 prose-th:p-2 prose-th:text-xs prose-th:font-semibold prose-th:border prose-th:border-slate-200
    prose-td:text-slate-600 prose-td:p-2 prose-td:text-xs prose-td:border prose-td:border-slate-200
    prose-blockquote:border-l-2 prose-blockquote:border-indigo-400 prose-blockquote:bg-indigo-50 prose-blockquote:py-2 prose-blockquote:px-4 prose-blockquote:rounded-r-lg prose-blockquote:my-4 prose-blockquote:not-italic
    prose-hr:border-slate-200 prose-hr:my-6
    prose-img:rounded-lg prose-img:border prose-img:border-slate-200`;
  const proseClasses = isDarkPreview ? darkProseClasses : lightProseClasses;
  return <div className="flex-1 flex h-full overflow-hidden bg-slate-950 animate-in fade-in duration-300" data-id="element-3820">
      {/* ── LEFT SIDEBAR: Spaces ── */}
      {!isFullscreen && <div className="w-64 border-r border-slate-800 bg-slate-900/30 flex flex-col shrink-0" data-id="element-3821">
          <div className="p-3 border-b border-slate-800 space-y-2" data-id="element-3822">
            <h3 className="text-sm font-bold text-slate-200 flex items-center gap-2" data-id="element-3823">
              <Folder size={16} className="text-indigo-400" data-id="element-3824" />
              Espacios
              {!spacesLoading && <span className="text-[10px] font-normal text-slate-500 ml-auto" data-id="element-3825">
                  {safeSpacesAll.length}
                </span>}
            </h3>
            <div className="relative" data-id="element-3826">
              <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-500" data-id="element-3827" />
              <input type="text" placeholder="Buscar espacio..." value={spaceSearch} onChange={e => setSpaceSearch(e.target.value)} className="w-full bg-slate-950 border border-slate-800 rounded-lg pl-8 pr-7 py-1.5 text-xs text-slate-200 placeholder-slate-600 focus:outline-none focus:ring-1 focus:ring-indigo-500/50 focus:border-indigo-500/50" data-id="element-3828" />
              {spaceSearch && <button onClick={() => setSpaceSearch('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300" data-id="element-3829">
                  <X size={12} data-id="element-3830" />
                </button>}
            </div>
          </div>
          <div className="flex-1 overflow-y-auto p-2 space-y-1" data-id="element-3831">
            {spacesLoading ? Array.from({
          length: 5
        }).map((_, i) => <div key={i} className="flex items-center gap-3 p-2 rounded-lg animate-pulse" data-id="element-3832">
                  <div className="w-6 h-6 rounded bg-slate-800 shrink-0" data-id="element-3833" />
                  <div className="h-4 bg-slate-800 rounded w-2/3" data-id="element-3834" />
                </div>) : spacesError ? <div className="p-4 text-xs text-red-400 bg-red-950/20 rounded-lg border border-red-900/30 m-2" data-id="element-3835">
                <AlertCircle size={14} className="mb-1" data-id="element-3836" />
                {spacesError}
              </div> : safeSpaces.length === 0 ? <div className="p-4 text-xs text-slate-500 text-center" data-id="element-3837">
                {spaceSearch ? 'Sin resultados para esa búsqueda.' : 'No se encontraron espacios.'}
              </div> : <>
                {/* Global / Team Spaces */}
                {globalSpaces.length > 0 && <div className="space-y-1" data-id="element-3838">
                    {personalSpaces.length > 0 && <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider px-2 pt-1 pb-0.5" data-id="element-3839">
                        Equipos
                      </p>}
                    {globalSpaces.map(space => <button key={space.id} onClick={() => {
              setSelectedSpace(space);
              setIsSearching(false);
              setSearchQuery('');
              setSelectedPage(null);
              setActiveFilter('Todos');
              setIsFullscreen(false);
            }} className={`w-full flex items-center gap-3 p-2 rounded-lg text-left transition-colors ${selectedSpace?.id === space.id && !isSearching ? 'bg-indigo-500/10 text-indigo-300 border border-indigo-500/20' : 'text-slate-400 hover:bg-slate-800 hover:text-slate-200 border border-transparent'}`} data-id="element-3840">
                        <div className={`w-6 h-6 rounded flex items-center justify-center text-[10px] font-bold shrink-0 ${selectedSpace?.id === space.id && !isSearching ? 'bg-indigo-500/20 text-indigo-400' : 'bg-slate-800 text-slate-500'}`} data-id="element-3841">
                          {space.name.charAt(0).toUpperCase()}
                        </div>
                        <div className="flex-1 min-w-0 flex items-center justify-between" data-id="element-3842">
                          <span className="text-sm truncate font-medium" data-id="element-3843">
                            {space.name}
                          </span>
                          {space.pageCount !== undefined && <span className="text-[10px] bg-slate-800 text-slate-400 px-1.5 py-0.5 rounded-full shrink-0" data-id="element-3844">
                              {space.pageCount}
                            </span>}
                        </div>
                      </button>)}
                  </div>}

                {/* Personal Spaces — collapsed by default */}
                {personalSpaces.length > 0 && <div className="space-y-1 mt-2" data-id="element-3845">
                    <button onClick={() => setShowPersonalSpaces(!showPersonalSpaces)} className="w-full flex items-center gap-1.5 px-2 py-1 text-[10px] font-semibold text-slate-500 uppercase tracking-wider hover:text-slate-400 transition-colors" data-id="element-3846">
                      {showPersonalSpaces ? <ChevronDown size={12} data-id="element-3847" /> : <ChevronRight size={12} data-id="element-3848" />}
                      <Users size={11} data-id="element-3849" />
                      Personales
                      <span className="ml-auto font-normal text-slate-600" data-id="element-3850">
                        {personalSpaces.length}
                      </span>
                    </button>
                    {showPersonalSpaces && personalSpaces.map(space => <button key={space.id} onClick={() => {
              setSelectedSpace(space);
              setIsSearching(false);
              setSearchQuery('');
              setSelectedPage(null);
              setActiveFilter('Todos');
              setIsFullscreen(false);
            }} className={`w-full flex items-center gap-3 p-2 rounded-lg text-left transition-colors ${selectedSpace?.id === space.id && !isSearching ? 'bg-indigo-500/10 text-indigo-300 border border-indigo-500/20' : 'text-slate-400 hover:bg-slate-800 hover:text-slate-200 border border-transparent'}`} data-id="element-3851">
                          <div className={`w-6 h-6 rounded flex items-center justify-center text-[10px] font-bold shrink-0 ${selectedSpace?.id === space.id && !isSearching ? 'bg-indigo-500/20 text-indigo-400' : 'bg-slate-800 text-slate-500'}`} data-id="element-3852">
                            {space.name.charAt(0).toUpperCase()}
                          </div>
                          <div className="flex-1 min-w-0 flex items-center justify-between" data-id="element-3853">
                            <span className="text-sm truncate font-medium" data-id="element-3854">
                              {space.name}
                            </span>
                            {space.pageCount !== undefined && <span className="text-[10px] bg-slate-800 text-slate-400 px-1.5 py-0.5 rounded-full shrink-0" data-id="element-3855">
                                {space.pageCount}
                              </span>}
                          </div>
                        </button>)}
                  </div>}

                {/* When searching and all results are personal, auto-expand */}
                {spaceSearch && globalSpaces.length === 0 && personalSpaces.length > 0 && !showPersonalSpaces && <button onClick={() => setShowPersonalSpaces(true)} className="w-full text-xs text-indigo-400 hover:text-indigo-300 py-2 text-center transition-colors" data-id="element-3856">
                      Mostrar {personalSpaces.length} espacios personales
                    </button>}
              </>}
          </div>
        </div>}

      {/* ── CENTER: Pages List ── */}
      {!isFullscreen && <div className="flex-1 flex flex-col min-w-0 bg-slate-950" data-id="element-3857">
          <div className="p-6 border-b border-slate-800 bg-slate-900/50 shrink-0" data-id="element-3858">
            <div className="max-w-3xl" data-id="element-3859">
              <h2 className="text-xl font-bold text-slate-100 flex items-center gap-2 mb-4" data-id="element-3860">
                <BookOpen className="text-indigo-400" data-id="element-3861" />
                {isSearching ? 'Resultados de búsqueda' : selectedSpace ? selectedSpace.name : 'Documentación'}
              </h2>
              <form onSubmit={handleSearch} className="relative" data-id="element-3862">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" data-id="element-3863" />

                <input type="text" placeholder="Buscar en Confluence..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full bg-slate-950 border border-slate-700 rounded-xl pl-10 pr-10 py-2.5 text-sm text-slate-200 placeholder:text-slate-500 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500/50 transition-all" data-id="element-3864" />

                {searchQuery && <button type="button" onClick={clearSearch} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300 p-1" data-id="element-3865">
                    <X size={14} data-id="element-3866" />
                  </button>}
              </form>
              {!isSearching && <div className="flex gap-2 mt-4 overflow-x-auto pb-1 scrollbar-none" data-id="element-3867">
                  {filterOptions.map(filter => <button key={filter} onClick={() => setActiveFilter(filter)} className={`px-3 py-1 rounded-full text-xs font-medium transition-colors border whitespace-nowrap ${activeFilter === filter ? 'bg-indigo-500/20 text-indigo-300 border-indigo-500/30' : 'bg-slate-800 hover:bg-slate-700 text-slate-300 border-slate-700'}`} data-id="element-3868">
                      {filter}
                    </button>)}
                </div>}
            </div>
          </div>

          <div className="flex-1 overflow-y-auto p-6" data-id="element-3869">
            <div className="max-w-5xl" data-id="element-3870">
              {pagesLoading ? <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-id="element-3871">
                  {Array.from({
              length: 6
            }).map((_, i) => <div key={i} className="bg-slate-900 border border-slate-800 rounded-xl p-4 h-36 animate-pulse" data-id="element-3872">
                      <div className="h-5 bg-slate-800 rounded w-3/4 mb-3" data-id="element-3873" />
                      <div className="h-3 bg-slate-800 rounded w-full mb-2" data-id="element-3874" />
                      <div className="h-3 bg-slate-800 rounded w-5/6" data-id="element-3875" />
                    </div>)}
                </div> : pagesError ? <div className="p-6 text-center bg-red-950/20 border border-red-900/30 rounded-xl" data-id="element-3876">
                  <AlertCircle size={24} className="mx-auto text-red-400 mb-2" data-id="element-3877" />

                  <p className="text-sm text-red-400" data-id="element-3878">{pagesError}</p>
                </div> : safePages.length === 0 ? <div className="py-12 text-center" data-id="element-3879">
                  <FileText size={32} className="mx-auto text-slate-700 mb-3" data-id="element-3880" />
                  <p className="text-sm text-slate-400" data-id="element-3881">
                    {isSearching ? 'No se encontraron resultados para tu búsqueda.' : 'Este espacio no tiene páginas que coincidan con el filtro.'}
                  </p>
                </div> : <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-id="element-3882">
                  {safePages.map(page => <button key={page.id} onClick={() => handlePageClick(page)} className={`text-left bg-slate-900 border rounded-xl p-4 transition-all hover:shadow-lg hover:shadow-black/20 group ${selectedPage?.id === page.id ? 'border-indigo-500 ring-1 ring-indigo-500/50' : 'border-slate-800 hover:border-slate-600'}`} data-id="element-3883">
                      <div className="flex items-start gap-3" data-id="element-3884">
                        <div className="w-8 h-8 rounded-lg bg-indigo-500/10 border border-indigo-500/20 flex items-center justify-center shrink-0 mt-0.5" data-id="element-3885">
                          <FileText size={14} className="text-indigo-400" data-id="element-3886" />
                        </div>
                        <div className="flex-1 min-w-0" data-id="element-3887">
                          <h4 className="font-bold text-slate-200 text-sm mb-1 line-clamp-2 group-hover:text-indigo-300 transition-colors" data-id="element-3888">
                            {page.title}
                          </h4>
                          {page.excerpt && page.excerpt !== '...' && page.excerpt.trim().length > 3 ? <p className="text-xs text-slate-500 line-clamp-2 leading-relaxed" data-id="element-3889">
                              {page.excerpt}
                            </p> : <p className="text-xs text-slate-600 italic" data-id="element-3890">
                              Hacé clic para ver el contenido
                            </p>}
                        </div>
                      </div>
                      {Array.isArray(page.labels) && page.labels.length > 0 && <div className="flex items-center gap-1.5 mt-3 flex-wrap" data-id="element-3891">
                          <Tag size={10} className="text-slate-600" data-id="element-3892" />
                          {page.labels.slice(0, 3).map(label => <span key={label} className="text-[9px] bg-indigo-500/10 text-indigo-300 px-1.5 py-0.5 rounded border border-indigo-500/20" data-id="element-3893">
                              {label}
                            </span>)}
                        </div>}
                      <div className="flex items-center justify-between mt-3 pt-3 border-t border-slate-800/50" data-id="element-3894">
                        <div className="flex items-center gap-2" data-id="element-3895">
                          <div className="w-5 h-5 rounded-full bg-gradient-to-br from-indigo-500/20 to-violet-500/20 border border-slate-700 flex items-center justify-center text-[8px] font-bold text-slate-300" data-id="element-3896">
                            {page.author?.charAt(0).toUpperCase() || '?'}
                          </div>
                          <span className="text-[10px] text-slate-400" data-id="element-3897">
                            {page.author || 'Unknown'}
                          </span>
                        </div>
                        {page.lastModified && <div className="flex items-center gap-1 text-[10px] text-slate-500" data-id="element-3898">
                            <Clock size={10} data-id="element-3899" />
                            {page.lastModified}
                          </div>}
                      </div>
                    </button>)}
                </div>}
            </div>
          </div>
        </div>}

      {/* ── RIGHT PANEL: Page Preview ── */}
      {showPreview && <>
          {/* Resize Handle */}
          {!isFullscreen && <div onMouseDown={handleResizeStart} className={`w-1.5 shrink-0 cursor-col-resize group relative flex items-center justify-center transition-colors ${isResizing ? 'bg-indigo-500/40' : 'bg-slate-800 hover:bg-indigo-500/30'}`} data-id="element-3900">
              <div className="absolute inset-y-0 -left-1 -right-1 z-10" data-id="element-3901" />
              <GripVertical size={12} className={`text-slate-600 group-hover:text-indigo-400 transition-colors ${isResizing ? 'text-indigo-400' : ''}`} data-id="element-3902" />
            </div>}

          <div style={{
        width: isFullscreen ? '100%' : `${previewWidth}px`
      }} className={`border-l flex flex-col shrink-0 shadow-2xl transition-all duration-200 ${isFullscreen ? 'absolute inset-0 z-50' : 'relative'} ${!isResizing ? 'animate-in slide-in-from-right-4 duration-200' : ''} ${isDarkPreview ? 'bg-slate-900 border-slate-800' : 'bg-white border-slate-200'}`} data-id="element-3903">
            {/* Preview Header */}
            <div className={`p-4 border-b flex items-start justify-between backdrop-blur-sm sticky top-0 z-10 ${isDarkPreview ? 'border-slate-800 bg-slate-900/80' : 'border-slate-200 bg-white/90'}`} data-id="element-3904">
              <div className="flex-1 min-w-0 pr-4" data-id="element-3905">
                <div className={`flex items-center gap-1.5 text-[10px] font-medium mb-1.5 ${isDarkPreview ? 'text-indigo-400' : 'text-indigo-600'}`} data-id="element-3906">
                  <span data-id="element-3907">{selectedPage.spaceKey || selectedSpace?.name}</span>
                  <ChevronRight size={10} data-id="element-3908" />
                  <span className={isDarkPreview ? 'text-slate-500' : 'text-slate-400'} data-id="element-3909">
                    Página
                  </span>
                </div>
                <h3 className={`font-bold text-lg leading-tight ${isDarkPreview ? 'text-slate-100' : 'text-slate-900'}`} data-id="element-3910">
                  {selectedPage.title}
                </h3>
              </div>
              <div className="flex items-center gap-1 shrink-0" data-id="element-3911">
                {/* Summarize Button */}
                {!pageLoading && !pageError && <SummarizeButton content={rawPageHtml || selectedPage.contentHtml} title={selectedPage.title} contextType="página de Confluence" variant="header" className="mr-1" data-id="element-3912" />}

                {/* Theme Toggle */}
                <div className={`flex items-center rounded-lg p-0.5 mr-1 ${isDarkPreview ? 'bg-slate-800' : 'bg-slate-100'}`} data-id="element-3913">
                  <button onClick={() => changeTheme('light')} className={`p-1.5 rounded-md transition-all ${previewTheme === 'light' ? isDarkPreview ? 'bg-slate-700 text-amber-400 shadow-sm' : 'bg-white text-amber-500 shadow-sm' : isDarkPreview ? 'text-slate-500 hover:text-slate-300' : 'text-slate-400 hover:text-slate-600'}`} title="Tema claro" data-id="element-3914">
                    <Sun size={13} data-id="element-3915" />
                  </button>
                  <button onClick={() => changeTheme('dark')} className={`p-1.5 rounded-md transition-all ${previewTheme === 'dark' ? isDarkPreview ? 'bg-slate-700 text-indigo-400 shadow-sm' : 'bg-white text-indigo-500 shadow-sm' : isDarkPreview ? 'text-slate-500 hover:text-slate-300' : 'text-slate-400 hover:text-slate-600'}`} title="Tema oscuro" data-id="element-3916">
                    <Moon size={13} data-id="element-3917" />
                  </button>
                  <button onClick={() => changeTheme('system')} className={`p-1.5 rounded-md transition-all ${previewTheme === 'system' ? isDarkPreview ? 'bg-slate-700 text-emerald-400 shadow-sm' : 'bg-white text-emerald-500 shadow-sm' : isDarkPreview ? 'text-slate-500 hover:text-slate-300' : 'text-slate-400 hover:text-slate-600'}`} title="Tema del sistema" data-id="element-3918">
                    <Monitor size={13} data-id="element-3919" />
                  </button>
                </div>

                {/* Fullscreen toggle */}
                <button onClick={() => setIsFullscreen(!isFullscreen)} className={`p-1.5 rounded-md transition-colors ${isDarkPreview ? 'text-slate-400 hover:text-indigo-400 hover:bg-slate-800' : 'text-slate-400 hover:text-indigo-600 hover:bg-slate-100'}`} title={isFullscreen ? 'Salir de pantalla completa (Esc)' : 'Pantalla completa'} data-id="element-3920">
                  {isFullscreen ? <Minimize2 size={16} data-id="element-3921" /> : <Maximize2 size={16} data-id="element-3922" />}
                </button>
                {selectedPage.url && <a href={selectedPage.url} target="_blank" rel="noopener noreferrer" className={`p-1.5 rounded-md transition-colors ${isDarkPreview ? 'text-slate-400 hover:text-indigo-400 hover:bg-slate-800' : 'text-slate-400 hover:text-indigo-600 hover:bg-slate-100'}`} title="Abrir en Confluence" data-id="element-3923">
                    <ExternalLink size={16} data-id="element-3924" />
                  </a>}
                <button onClick={() => {
              setSelectedPage(null);
              setIsFullscreen(false);
              setRawPageHtml('');
            }} className={`p-1.5 rounded-md transition-colors ${isDarkPreview ? 'text-slate-400 hover:text-slate-200 hover:bg-slate-800' : 'text-slate-400 hover:text-slate-600 hover:bg-slate-100'}`} data-id="element-3925">
                  <X size={16} data-id="element-3926" />
                </button>
              </div>
            </div>

            {/* Preview Content */}
            <div className={`flex-1 overflow-y-auto custom-scrollbar relative ${isFullscreen ? 'p-8 max-w-4xl mx-auto w-full' : 'p-6'} ${isDarkPreview ? '' : 'bg-white'}`} data-id="element-3927">
              {pageLoading ? <div className="space-y-4 animate-pulse" data-id="element-3928">
                  <div className={`h-6 rounded w-3/4 ${isDarkPreview ? 'bg-slate-800' : 'bg-slate-200'}`} data-id="element-3929" />

                  <div className={`h-4 rounded w-full ${isDarkPreview ? 'bg-slate-800' : 'bg-slate-200'}`} data-id="element-3930" />

                  <div className={`h-4 rounded w-full ${isDarkPreview ? 'bg-slate-800' : 'bg-slate-200'}`} data-id="element-3931" />

                  <div className={`h-4 rounded w-5/6 ${isDarkPreview ? 'bg-slate-800' : 'bg-slate-200'}`} data-id="element-3932" />

                  <div className={`h-32 rounded w-full my-6 ${isDarkPreview ? 'bg-slate-800' : 'bg-slate-200'}`} data-id="element-3933" />

                  <div className={`h-4 rounded w-full ${isDarkPreview ? 'bg-slate-800' : 'bg-slate-200'}`} data-id="element-3934" />

                  <div className={`h-4 rounded w-4/5 ${isDarkPreview ? 'bg-slate-800' : 'bg-slate-200'}`} data-id="element-3935" />
                </div> : pageError ? <div className="text-center py-10" data-id="element-3936">
                  <AlertCircle size={24} className="mx-auto text-red-400 mb-2" data-id="element-3937" />

                  <p className="text-sm text-red-400" data-id="element-3938">{pageError}</p>
                </div> : <div className={`${isDarkPreview ? `confluence-dark ${darkProseClasses}` : `confluence-light ${lightProseClasses}`}`} dangerouslySetInnerHTML={{
            __html: selectedPage.contentHtml
          }} data-id="element-3939" />}
            </div>
          </div>
        </>}
    </div>;
}